/**
 * The colour a connector's chip wears.
 *
 * A tagged app reads faster when its chip carries the brand's colour beside
 * the mark — Slack's aubergine, Gmail's red, HubSpot's orange — instead of one
 * tint for every app. The choice is catalogue data, not client code: each
 * connector's `metadata.accent` names a colour family, seeded in
 * `packages/database` and served on connection and integration views. This
 * module only maps a family to classes — full strings, never composed, since
 * Tailwind emits only the classes it can read in the source — and ignores a
 * family it does not know.
 *
 * @module lib/connectorAccent
 */

export interface ConnectorAccent {
  /** Composer chip: border, wash and text, scaled by the surrounding text. */
  readonly chip: string;
}

// One literal string per family: Tailwind emits only the classes it can read
// verbatim in the source, so nothing here is assembled at runtime.
const FAMILIES: Readonly<Record<string, ConnectorAccent>> = {
  amber: { chip: "border-amber-500/25 bg-amber-500/12 text-amber-700 dark:text-amber-300" },
  blue: { chip: "border-blue-500/25 bg-blue-500/12 text-blue-700 dark:text-blue-300" },
  cyan: { chip: "border-cyan-500/25 bg-cyan-500/12 text-cyan-700 dark:text-cyan-300" },
  emerald: {
    chip: "border-emerald-500/25 bg-emerald-500/12 text-emerald-700 dark:text-emerald-300",
  },
  fuchsia: {
    chip: "border-fuchsia-500/25 bg-fuchsia-500/12 text-fuchsia-700 dark:text-fuchsia-300",
  },
  green: { chip: "border-green-500/25 bg-green-500/12 text-green-700 dark:text-green-300" },
  indigo: { chip: "border-indigo-500/25 bg-indigo-500/12 text-indigo-700 dark:text-indigo-300" },
  lime: { chip: "border-lime-500/25 bg-lime-500/12 text-lime-700 dark:text-lime-300" },
  orange: { chip: "border-orange-500/25 bg-orange-500/12 text-orange-700 dark:text-orange-300" },
  pink: { chip: "border-pink-500/25 bg-pink-500/12 text-pink-700 dark:text-pink-300" },
  purple: { chip: "border-purple-500/25 bg-purple-500/12 text-purple-700 dark:text-purple-300" },
  red: { chip: "border-red-500/25 bg-red-500/12 text-red-700 dark:text-red-300" },
  rose: { chip: "border-rose-500/25 bg-rose-500/12 text-rose-700 dark:text-rose-300" },
  sky: { chip: "border-sky-500/25 bg-sky-500/12 text-sky-700 dark:text-sky-300" },
  teal: { chip: "border-teal-500/25 bg-teal-500/12 text-teal-700 dark:text-teal-300" },
  violet: { chip: "border-violet-500/25 bg-violet-500/12 text-violet-700 dark:text-violet-300" },
  yellow: { chip: "border-yellow-500/25 bg-yellow-500/12 text-yellow-700 dark:text-yellow-300" },
  zinc: { chip: "border-zinc-500/25 bg-zinc-500/12 text-zinc-700 dark:text-zinc-300" },
};

/** Apps without a colour of their own share one quiet tint. */
export const DEFAULT_CONNECTOR_ACCENT: ConnectorAccent = FAMILIES.emerald!;

/** The classes for a colour family from the catalogue; unknown or unset reads as the default. */
export function resolveConnectorAccent(family: string | null | undefined): ConnectorAccent {
  if (!family) return DEFAULT_CONNECTOR_ACCENT;
  return (
    (Object.hasOwn(FAMILIES, family) ? FAMILIES[family] : undefined) ?? DEFAULT_CONNECTOR_ACCENT
  );
}

/** The family a connection view's catalogue metadata names, if any. */
export function connectorAccentFamily(
  metadata: Record<string, unknown> | null | undefined,
): string | null {
  const family = metadata?.accent;
  return typeof family === "string" && family.length > 0 ? family : null;
}
